/**
 * useConversations Hook
 *
 * Generic conversation management hook for chat interfaces.
 * Combines React Query for data fetching with @ai-sdk/react for streaming.
 *
 * @module
 * @see packages/db/src/repositories/conversations.ts - Database layer
 * @see apps/api/src/conversations/routes.ts - API endpoints
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// ============================================================================
// Types
// ============================================================================

type ConversationEntityType =
  | 'workflow'
  | 'docs'
  | 'property'
  | 'global'
  | 'environment-instance'
  | 'flow-planner' // historical only — the Flow Planner was removed
  | 'user-events-admin'
  | 'run-chat'
  | 'assistant';
type ConversationStatus = 'active' | 'archived';
export type ConversationMessageRole = 'system' | 'user' | 'assistant' | 'tool';
export type ConversationMessageFields = 'full' | 'summary';

interface Conversation {
  id: string;
  orgId: string;
  createdBy: string;
  entityType: ConversationEntityType;
  entityId: string | null;
  title: string | null;
  status: ConversationStatus;
  metadata: Record<string, unknown> | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationMessage {
  id: string;
  orgId: string;
  conversationId: string;
  clientMessageId: string | null;
  role: ConversationMessageRole;
  content: string | null;
  parts: unknown[] | null;
  toolName: string | null;
  toolCallId: string | null;
  toolInput: Record<string, unknown> | null;
  toolOutput: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface ConversationListItem extends Conversation {
  latestMessage: {
    id: string;
    role: ConversationMessageRole;
    content: string | null;
    toolName: string | null;
    toolCallId: string | null;
    clientMessageId: string | null;
    createdAt: string;
  } | null;
  messageCount: number;
}

interface PaginatedResponse<T> {
  ok: boolean;
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

interface SingleResponse<T> {
  ok: boolean;
  data: T;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchConversations(params: {
  entityType: ConversationEntityType;
  entityId?: string | null;
  includeArchived?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<PaginatedResponse<ConversationListItem>> {
  const searchParams = new URLSearchParams();
  searchParams.set('entityType', params.entityType);
  if (params.entityId) searchParams.set('entityId', params.entityId);
  if (params.includeArchived) searchParams.set('includeArchived', 'true');
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));

  const response = await fetch(`${API_URL}/conversations?${searchParams}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to fetch conversations');
  }

  return response.json();
}

async function fetchConversationByEntity(params: {
  entityType: ConversationEntityType;
  entityId?: string | null;
}): Promise<Conversation | null> {
  const searchParams = new URLSearchParams();
  searchParams.set('entityType', params.entityType);
  if (params.entityId) searchParams.set('entityId', params.entityId);

  const response = await fetch(`${API_URL}/conversations/by-entity?${searchParams}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to find conversation');
  }

  const data = (await response.json()) as SingleResponse<Conversation | null>;
  return data.data;
}

export function buildConversationMessagesUrl(params: {
  apiUrl: string;
  conversationId: string;
  page?: number;
  pageSize?: number;
  order?: 'oldest' | 'latest';
  fields?: ConversationMessageFields;
}): string {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.order) searchParams.set('order', params.order);
  if (params.fields) searchParams.set('fields', params.fields);

  return searchParams.toString()
    ? `${params.apiUrl}/conversations/${params.conversationId}/messages?${searchParams}`
    : `${params.apiUrl}/conversations/${params.conversationId}/messages`;
}

async function fetchMessages(
  conversationId: string,
  params?: {
    page?: number;
    pageSize?: number;
    order?: 'oldest' | 'latest';
    fields?: ConversationMessageFields;
  }
): Promise<PaginatedResponse<ConversationMessage>> {
  const url = buildConversationMessagesUrl({
    apiUrl: API_URL,
    conversationId,
    ...params,
  });

  const response = await fetch(url, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to fetch messages');
  }

  return response.json();
}

async function createConversation(params: {
  id?: string;
  entityType: ConversationEntityType;
  entityId?: string | null;
  title?: string | null;
}): Promise<Conversation> {
  const response = await fetch(`${API_URL}/conversations`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to create conversation');
  }

  const data = (await response.json()) as SingleResponse<Conversation>;
  return data.data;
}

async function getOrCreateConversation(params: {
  id: string;
  entityType: ConversationEntityType;
  entityId?: string | null;
  title?: string;
}): Promise<Conversation> {
  const response = await fetch(`${API_URL}/conversations/get-or-create`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to get or create conversation');
  }

  const data = (await response.json()) as SingleResponse<Conversation>;
  return data.data;
}

async function archiveConversation(id: string): Promise<Conversation> {
  const response = await fetch(`${API_URL}/conversations/${id}/archive`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to archive conversation');
  }

  const data = (await response.json()) as SingleResponse<Conversation>;
  return data.data;
}

async function createMessage(
  conversationId: string,
  params: {
    clientMessageId?: string | null;
    role: ConversationMessageRole;
    content?: string | null;
    parts?: unknown[] | null;
    toolName?: string | null;
    toolCallId?: string | null;
    toolInput?: Record<string, unknown> | null;
    toolOutput?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
  }
): Promise<ConversationMessage> {
  const response = await fetch(`${API_URL}/conversations/${conversationId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to create message');
  }

  const data = (await response.json()) as SingleResponse<ConversationMessage>;
  return data.data;
}

async function createMessages(
  conversationId: string,
  messages: Array<{
    clientMessageId?: string | null;
    role: ConversationMessageRole;
    content?: string | null;
    parts?: unknown[] | null;
    toolName?: string | null;
    toolCallId?: string | null;
    toolInput?: Record<string, unknown> | null;
    toolOutput?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
  }>
): Promise<ConversationMessage[]> {
  const response = await fetch(`${API_URL}/conversations/${conversationId}/messages/batch`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages }),
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to create messages');
  }

  const data = (await response.json()) as SingleResponse<ConversationMessage[]>;
  return data.data;
}

// ============================================================================
// Query Keys
// ============================================================================

const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (entityType: string, entityId?: string | null) =>
    [...conversationKeys.lists(), { entityType, entityId }] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
  byEntity: (entityType: string, entityId?: string | null) =>
    [...conversationKeys.all, 'by-entity', { entityType, entityId }] as const,
  messagesRoot: (conversationId: string) =>
    [...conversationKeys.all, 'messages', conversationId] as const,
  messages: (
    conversationId: string,
    params?: {
      pageSize?: number;
      order?: 'oldest' | 'latest';
      fields?: ConversationMessageFields;
    }
  ) => [...conversationKeys.messagesRoot(conversationId), params ?? {}] as const,
};

// ============================================================================
// Hook Options
// ============================================================================

type UseConversationsOptions = {
  /** The type of entity to scope conversations to */
  entityType: ConversationEntityType;
  /** Optional entity ID for scoping (e.g., workflow ID) */
  entityId?: string | null;
  /** Enable/disable the hook */
  enabled?: boolean;
  /** Single conversation mode - auto-creates/loads one conversation per entity */
  singleConversation?: boolean;
  /** Number of messages to hydrate for the active conversation */
  messagePageSize?: number;
  /** Which side of the conversation history to hydrate */
  messageOrder?: 'oldest' | 'latest';
  /** Select full persisted message payloads or compact readable history */
  messageFields?: ConversationMessageFields;
};

// ============================================================================
// Hook
// ============================================================================

export function useConversations({
  entityType,
  entityId,
  enabled = true,
  singleConversation = false,
  messagePageSize = 200,
  messageOrder = 'oldest',
  messageFields = 'full',
}: UseConversationsOptions) {
  const queryClient = useQueryClient();
  const [activeConversationId, setActiveConversationId] = React.useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Conversation List Query (disabled in single conversation mode)
  // ---------------------------------------------------------------------------

  const conversationsQuery = useQuery({
    queryKey: conversationKeys.list(entityType, entityId),
    queryFn: () => fetchConversations({ entityType, entityId }),
    enabled: enabled && !singleConversation,
    staleTime: 30_000,
  });

  // ---------------------------------------------------------------------------
  // Single Conversation Query (only in single conversation mode)
  // ---------------------------------------------------------------------------

  const singleConversationQuery = useQuery({
    queryKey: conversationKeys.byEntity(entityType, entityId),
    queryFn: () => fetchConversationByEntity({ entityType, entityId }),
    enabled: enabled && singleConversation,
    staleTime: 30_000,
  });

  // ---------------------------------------------------------------------------
  // Active Conversation Messages Query
  // ---------------------------------------------------------------------------

  const effectiveConversationId = singleConversation
    ? (singleConversationQuery.data?.id ?? null)
    : activeConversationId;

  const messagesQuery = useQuery({
    queryKey: conversationKeys.messages(effectiveConversationId ?? '', {
      pageSize: messagePageSize,
      order: messageOrder,
      fields: messageFields,
    }),
    queryFn: () =>
      fetchMessages(effectiveConversationId!, {
        pageSize: messagePageSize,
        order: messageOrder,
        fields: messageFields,
      }),
    enabled: enabled && !!effectiveConversationId,
    staleTime: 10_000,
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createConversationMutation = useMutation({
    mutationFn: createConversation,
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: conversationKeys.byEntity(entityType, entityId),
      });
      setActiveConversationId(conversation.id);
    },
  });

  const getOrCreateConversationMutation = useMutation({
    mutationFn: getOrCreateConversation,
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: conversationKeys.byEntity(entityType, entityId),
      });
      setActiveConversationId(conversation.id);
    },
  });

  const archiveConversationMutation = useMutation({
    mutationFn: archiveConversation,
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: conversationKeys.byEntity(entityType, entityId),
      });
      if (activeConversationId === conversation.id) {
        setActiveConversationId(null);
      }
    },
  });

  const createMessageMutation = useMutation({
    mutationFn: ({
      conversationId,
      ...params
    }: Parameters<typeof createMessage>[1] & { conversationId: string }) =>
      createMessage(conversationId, params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.messagesRoot(variables.conversationId),
      });
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });

  const createMessagesMutation = useMutation({
    mutationFn: ({
      conversationId,
      messages,
    }: {
      conversationId: string;
      messages: Parameters<typeof createMessages>[1];
    }) => createMessages(conversationId, messages),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.messagesRoot(variables.conversationId),
      });
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });

  // ---------------------------------------------------------------------------
  // Derived State
  // ---------------------------------------------------------------------------

  const conversations = conversationsQuery.data?.data ?? [];
  const activeConversation = singleConversation
    ? (singleConversationQuery.data ?? null)
    : (conversations.find((c) => c.id === activeConversationId) ?? null);
  const messages = messagesQuery.data?.data ?? [];

  const isLoadingConversations = singleConversation
    ? singleConversationQuery.isLoading
    : conversationsQuery.isLoading;

  const isLoadingMessages = messagesQuery.isLoading;

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const createNewConversation = React.useCallback(
    async (title?: string) => {
      const id = crypto.randomUUID();
      return createConversationMutation.mutateAsync({
        id,
        entityType,
        entityId,
        title,
      });
    },
    [createConversationMutation, entityType, entityId]
  );

  const getOrCreateSingleConversation = React.useCallback(
    async (title?: string) => {
      const id = crypto.randomUUID();
      return getOrCreateConversationMutation.mutateAsync({
        id,
        entityType,
        entityId,
        title,
      });
    },
    [getOrCreateConversationMutation, entityType, entityId]
  );

  const archiveActiveConversation = React.useCallback(async () => {
    if (!effectiveConversationId) return;
    return archiveConversationMutation.mutateAsync(effectiveConversationId);
  }, [archiveConversationMutation, effectiveConversationId]);

  const refreshMessages = React.useCallback(() => {
    if (effectiveConversationId) {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.messagesRoot(effectiveConversationId),
      });
    }
  }, [queryClient, effectiveConversationId]);

  const addMessage = React.useCallback(
    async (params: Omit<Parameters<typeof createMessage>[1], 'conversationId'>) => {
      if (!effectiveConversationId) {
        throw new Error('No active conversation');
      }
      return createMessageMutation.mutateAsync({
        conversationId: effectiveConversationId,
        ...params,
      });
    },
    [createMessageMutation, effectiveConversationId]
  );

  const addMessages = React.useCallback(
    async (msgs: Parameters<typeof createMessages>[1]) => {
      if (!effectiveConversationId) {
        throw new Error('No active conversation');
      }
      return createMessagesMutation.mutateAsync({
        conversationId: effectiveConversationId,
        messages: msgs,
      });
    },
    [createMessagesMutation, effectiveConversationId]
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // Conversation list
    conversations,
    isLoadingConversations,

    // Active conversation
    activeConversationId: effectiveConversationId,
    setActiveConversationId,
    activeConversation,

    // Messages
    messages,
    isLoadingMessages,
    refreshMessages,

    // Mutations
    createConversation: createNewConversation,
    getOrCreateConversation: getOrCreateSingleConversation,
    archiveConversation: archiveActiveConversation,
    addMessage,
    addMessages,
    isCreatingConversation:
      createConversationMutation.isPending || getOrCreateConversationMutation.isPending,
    isArchiving: archiveConversationMutation.isPending,
    isSavingMessage: createMessageMutation.isPending || createMessagesMutation.isPending,

    // Query client utilities for external invalidation
    invalidateConversations: () =>
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() }),
    invalidateMessages: () => {
      if (effectiveConversationId) {
        queryClient.invalidateQueries({
          queryKey: conversationKeys.messagesRoot(effectiveConversationId),
        });
      }
    },
  };
}
